import { ArrowDown, ArrowUp, ArrowUpDown, HelpCircle } from "lucide-react";
import { useMemo, useState } from "react";
import type { BuyLogRowPatch } from "@/components/buy-log/BuyLogRow";
import { BuyLogRow } from "@/components/buy-log/BuyLogRow";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { BudgetAwardStatus, BudgetLineItem } from "@/hooks/use-budgets";
import type { Company } from "@/hooks/use-companies";
import { toNumber } from "@/lib/formatters";

interface BuyLogGridProps {
  lineItems: BudgetLineItem[];
  companies: Company[];
  canEdit: boolean;
  onPatchLine: (lineId: string, patch: BuyLogRowPatch) => void;
  onDeleteLine: (lineId: string) => void;
  onCreateCommitment: (line: BudgetLineItem) => void;
  onViewHistory?: (line: BudgetLineItem) => void;
  onViewCommitment?: (commitmentId: string) => void;
  recentlyCommittedLineId?: string | null;
}

type SortKey =
  | "costCode"
  | "description"
  | "scheduledValue"
  | "awardValue"
  | "allowance"
  | "holdsAtBuy"
  | "totalAward"
  | "variance"
  | "awardStatus"
  | "isAllowance"
  | "subcontractor"
  | "commitment";

type SortDir = "asc" | "desc";

const AWARD_STATUS_ORDER: Record<BudgetAwardStatus, number> = {
  not_awarded: 0,
  partial: 1,
  awarded: 2,
};

function compareNullable<T>(
  a: T | null | undefined,
  b: T | null | undefined,
  cmp: (a: T, b: T) => number,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return cmp(a, b);
}

function getSortValue(item: BudgetLineItem, key: SortKey): number | string | null {
  switch (key) {
    case "costCode":
      return item.costCode?.code ?? null;
    case "description":
      return item.description;
    case "scheduledValue":
      return item.scheduledValueText ? toNumber(item.scheduledValueText) : null;
    case "awardValue":
      return toNumber(item.awardValue);
    case "allowance":
      return toNumber(item.allowance);
    case "holdsAtBuy":
      return toNumber(item.holdsAtBuy);
    case "totalAward":
      return toNumber(item.awardValue) + toNumber(item.allowance) + toNumber(item.holdsAtBuy);
    case "variance":
      return item.variance ? toNumber(item.variance) : null;
    case "awardStatus":
      return AWARD_STATUS_ORDER[item.awardStatus];
    case "isAllowance":
      return item.isAllowance ? 1 : 0;
    case "subcontractor":
      return item.awardedCompany?.companyName ?? null;
    case "commitment":
      return item.commitment?.commitmentNumber ?? null;
  }
}

function compareItems(a: BudgetLineItem, b: BudgetLineItem, key: SortKey): number {
  const va = getSortValue(a, key);
  const vb = getSortValue(b, key);
  return compareNullable(va, vb, (x, y) => {
    if (typeof x === "number" && typeof y === "number") return x - y;
    return String(x).localeCompare(String(y), undefined, { numeric: true });
  });
}

interface SortableHeadProps {
  sortKey: SortKey;
  label: string;
  align?: "left" | "right" | "center";
  active: SortKey | null;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  tooltip?: string;
}

function SortableHead({
  sortKey,
  label,
  align = "left",
  active,
  dir,
  onSort,
  tooltip,
}: SortableHeadProps) {
  const isActive = active === sortKey;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  const alignClass =
    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const justifyClass =
    align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <TableHead
      className={`text-xs font-semibold uppercase tracking-wider text-gray-500 ${alignClass}`}
    >
      <div className={`inline-flex w-full items-center gap-1 ${justifyClass}`}>
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className="inline-flex items-center gap-1 hover:text-gray-900"
        >
          <span>{label}</span>
          <Icon className={`h-3 w-3 ${isActive ? "text-gray-900" : "text-gray-400"}`} />
        </button>
        {tooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger className="inline-flex cursor-help">
                <HelpCircle className="h-3 w-3 text-gray-400 hover:text-gray-600" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
    </TableHead>
  );
}

/**
 * Flat spreadsheet-style view of the Buy Log.
 */
export function BuyLogGrid({
  lineItems,
  companies,
  canEdit,
  onPatchLine,
  onDeleteLine,
  onCreateCommitment,
  onViewHistory,
  onViewCommitment,
  recentlyCommittedLineId,
}: BuyLogGridProps) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  const sortedItems = useMemo(() => {
    if (!sortKey) return lineItems;
    const copy = [...lineItems];
    copy.sort((a, b) => {
      const result = compareItems(a, b, sortKey);
      return sortDir === "asc" ? result : -result;
    });
    return copy;
  }, [lineItems, sortKey, sortDir]);

  const headProps = { active: sortKey, dir: sortDir, onSort: handleSort };

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
      <Table>
        <TableHeader className="bg-gray-50">
          <TableRow>
            <SortableHead sortKey="costCode" label="Cost Code" {...headProps} />
            <SortableHead sortKey="description" label="Description" {...headProps} />
            <SortableHead
              sortKey="scheduledValue"
              label="Scheduled Value"
              align="right"
              {...headProps}
            />
            <SortableHead sortKey="awardValue" label="Award" align="right" {...headProps} />
            <SortableHead
              sortKey="allowance"
              label="Allowance"
              align="right"
              {...headProps}
              tooltip="Allowance amount — a lump-sum reserve for undefined scope, separate from the base award value."
            />
            <SortableHead sortKey="holdsAtBuy" label="Holds" align="right" {...headProps} />
            <SortableHead sortKey="totalAward" label="Total Award" align="right" {...headProps} />
            <SortableHead sortKey="variance" label="Variance" align="right" {...headProps} />
            <SortableHead sortKey="awardStatus" label="Award Status" {...headProps} />
            <SortableHead
              sortKey="isAllowance"
              label="ALW"
              align="center"
              {...headProps}
              tooltip="Marks this line as an AIA allowance. Flows through to the G703 schedule of values as an allowance line."
            />
            <SortableHead sortKey="subcontractor" label="Subcontractor" {...headProps} />
            <SortableHead sortKey="commitment" label="Commitment" {...headProps} />
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((item) => (
            <BuyLogRow
              key={item.id}
              lineItem={item}
              companies={companies}
              canEdit={canEdit}
              onPatch={(patch) => onPatchLine(item.id, patch)}
              onDelete={() => onDeleteLine(item.id)}
              onCreateCommitment={() => onCreateCommitment(item)}
              onViewHistory={onViewHistory ? () => onViewHistory(item) : undefined}
              onViewCommitment={onViewCommitment}
              recentlyCommitted={recentlyCommittedLineId === item.id}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
